import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import CategoryManager from "./CategoryManager";
import AddProductForm from "./AddProductForm";
import {
  listProducts,
  deleteProduct,
  setProductActive,
  importStaticProducts,
} from "./productsService";
// Read-only — used only to seed Firestore once. Nothing here touches the
// static file itself or how the homepage uses it.
import StaticProductsData from "../../data/products/index";
import "./adminCatalog.scss";
import "./adminCatalogAdditions.css";

export default function AdminCatalogPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState(null);
  const [busyId, setBusyId] = useState(null);

  // Clicking a product row opens the editor above the list, which on a long
  // catalog can land off-screen — it looked like the click did nothing. The
  // ref scrolls the editor to you, and closeForm scrolls you back to the list
  // you came from.
  const formRef = useRef(null);
  const listRef = useRef(null);

  const refresh = async () => {
    const list = await listProducts();
    setProducts(list);
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleImportStatic = async () => {
    setImporting(true);
    setImportMessage(null);
    try {
      const { imported, skipped } = await importStaticProducts(StaticProductsData);
      const parts = [];
      if (imported.length) parts.push(`Imported: ${imported.join(", ")}`);
      if (skipped.length) parts.push(`Already in catalog (skipped): ${skipped.join(", ")}`);
      setImportMessage(parts.join(" · ") || "Nothing to import.");
      await refresh();
    } catch (err) {
      setImportMessage(`Error: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    if (showForm) {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [showForm, editingProduct]);

  // One way out of the editor, used by the header ×, the footer Cancel and the
  // Escape key, so all three leave the page in the same state.
  const closeForm = () => {
    setShowForm(false);
    setEditingProduct(null);
    listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSaved = () => {
    setShowForm(false);
    setEditingProduct(null);
    refresh();
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setShowForm(true);
  };

  // Hide, not delete, is the right default. Past orders reference the product
  // document; deleting it leaves those orders pointing at nothing. Hiding
  // takes it off the storefront immediately and is reversible.
  const handleToggleActive = async (product) => {
    setBusyId(product.id);
    try {
      await setProductActive(product.id, product.active === false);
      await refresh();
    } finally {
      setBusyId(null);
    }
  };

  // Imported products use a fixed document ID (`static-{slug}`), so deleting
  // one and pressing "Import Existing TAGGG Products" again brings it
  // straight back. That's worth saying out loud before the click, not after.
  const handleDelete = async (product) => {
    const imported = Boolean(product.importedFrom);
    const warning = imported
      ? `Delete "${product.name}" permanently?\n\nThis product was imported, so pressing "Import Existing TAGGG Products" again will recreate it from the static file. To take it down for good, hide it instead.`
      : `Delete "${product.name}" permanently? This can't be undone. Hiding it is reversible.`;

    if (!window.confirm(warning)) return;

    setBusyId(product.id);
    try {
      await deleteProduct(product.id);
      await refresh();
    } finally {
      setBusyId(null);
    }
  };

  // "3 colors · 5 sizes · 1 hidden" — enough to see at a glance whether a
  // color you meant to take down actually went.
  const summarise = (p) => {
    const variants = p.variants || [];
    const hiddenVariants = variants.filter((v) => v.active === false).length;
    const hiddenSizes = variants.reduce(
      (sum, v) => sum + (v.sizes || []).filter((s) => s.hidden === true).length,
      0
    );
    const parts = [`${variants.length} color${variants.length === 1 ? "" : "s"}`];
    if (p.hasSizes) parts.push(`${(p.selectedSizes || []).length} sizes`);
    if (hiddenVariants) parts.push(`${hiddenVariants} color hidden`);
    if (hiddenSizes) parts.push(`${hiddenSizes} size/color hidden`);
    return parts.join(" · ");
  };

  return (
    <div className="admin-catalog-page">
      <div className="admin-page-header">
        <h2>Product Catalog</h2>
        <button
          type="button"
          className="admin-back-btn"
          onClick={() => navigate("/admin")}
        >
          ← Admin Dashboard
        </button>
      </div>

      <CategoryManager />

      <div className="admin-panel">
        <h3>Import Existing Products</h3>
        <p className="admin-hint">
          Brings your existing TAGGG tees/tanks into this catalog so you can set their category,
          edit stock, and mark colors/sizes as sold out. Safe to click more than once — anything
          already imported gets skipped, not duplicated.
        </p>
        <button
          type="button"
          className="admin-add-product-btn"
          onClick={handleImportStatic}
          disabled={importing}
        >
          {importing ? "Importing…" : "Import Existing TAGGG Products"}
        </button>
        {importMessage && <p className="admin-hint">{importMessage}</p>}
      </div>

      {!showForm && (
        <button
          type="button"
          className="admin-add-product-btn"
          onClick={() => { setEditingProduct(null); setShowForm(true); }}
        >
          + Add Product
        </button>
      )}

      {showForm && (
        <div ref={formRef}>
          <AddProductForm
            // Remounts the form when you switch products. Without this the form
            // keeps the previous product's state, because every useState here
            // reads editingProduct only on first mount.
            key={editingProduct?.id || "new"}
            editingProduct={editingProduct}
            onSaved={handleSaved}
            onCancel={closeForm}
          />
        </div>
      )}

      <div className="admin-panel" ref={listRef}>
        <h3>Existing Products ({products.length})</h3>
        <div className="admin-product-list">
          {products.map((p) => (
            <div
              className={`admin-product-row admin-product-row--clickable ${
                p.active === false ? "is-inactive" : ""
              }`}
              key={p.id}
              onClick={() => handleEdit(p)}
            >
              {p.image && <img src={p.image} alt={p.name} />}
              <div className="admin-product-row-info">
                <strong>
                  {p.name}
                  {p.active === false && (
                    <span className="admin-badge admin-badge--hidden">Hidden</span>
                  )}
                  {p.importedFrom && (
                    <span className="admin-badge admin-badge--imported">Imported</span>
                  )}
                </strong>
                <span>{summarise(p)}</span>
              </div>
              <div className="admin-product-row-actions">
                <button
                  type="button"
                  disabled={busyId === p.id}
                  onClick={(e) => { e.stopPropagation(); handleToggleActive(p); }}
                >
                  {p.active === false ? "Show" : "Hide"}
                </button>
                <button
                  type="button"
                  className="admin-danger-btn"
                  disabled={busyId === p.id}
                  onClick={(e) => { e.stopPropagation(); handleDelete(p); }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
